import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { countChildItems, deleteStagingLines, hasDescendantLines } from "@shared/dtoolsStagingLineMutations";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";
import { Trash2 } from "lucide-react";
import { useState } from "react";

type Props = {
  line: DtoolsStagingLine;
  lines: DtoolsStagingLine[];
  onLinesChange: (lines: DtoolsStagingLine[]) => void;
  compact?: boolean;
};

export default function DtoolsReviewLineDeleteButton({ line, lines, onLinesChange, compact }: Props) {
  const [open, setOpen] = useState(false);
  const counts = countChildItems(line.id, lines);
  const hasChildren = hasDescendantLines(line.id, lines);

  const handleConfirm = () => {
    const next = deleteStagingLines(lines, [line.id], { cascade: hasChildren });
    onLinesChange(next);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        size={compact ? "sm" : "sm"}
        variant="ghost"
        className="shrink-0 text-destructive hover:text-destructive"
        aria-label="Delete item"
        onClick={() => setOpen(true)}
      >
        <Trash2 className="h-3.5 w-3.5" />
        {!compact ? <span className="ml-1">Delete</span> : null}
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {hasChildren ? (
                  <>
                    <p>This item contains:</p>
                    <ul className="list-disc pl-5">
                      {counts.children > 0 ? <li>{counts.children} child items</li> : null}
                      {counts.accessories > 0 ? <li>{counts.accessories} accessories</li> : null}
                    </ul>
                    <p>This action will remove all related items.</p>
                  </>
                ) : (
                  <p>Are you sure you want to remove this item?</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
